import React, { useState, useEffect } from "react";
import {
  Container,
  Form,
  Button,
  Table,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Modal,
  Spinner,
} from "react-bootstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList,
} from "recharts";
import {
  FaUserPlus,
  FaUsers,
  FaChartPie,
  FaGlobeAmericas,
  FaEraser,
  FaFileExcel,
} from "react-icons/fa";

function DataFormPage() {
  // Estados para el formulario
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    pais: "",
  });

  // Estado para manejar las categorías seleccionadas y sus transponders { "Categoría": "N° TX" }
  const [inscripciones, setInscripciones] = useState({});

  // Estados para la lista y UI
  const [pilotos, setPilotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });

  const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" && window.location.port === "3000" ? "http://localhost:5000/api" : "/api");

  const categoriasDisponibles = ["1/8 Buggy", "1/8 Truggy", "1/8 Electrico"];

  // 1. Cargar datos al iniciar
  useEffect(() => {
    fetchPilotos();
  }, []);

  const fetchPilotos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/datos`);
      const data = await response.json();
      setPilotos(data);
      setLoading(false);
    } catch (error) {
      console.error("Error al cargar pilotos:", error);
      setLoading(false);
    }
  };

  // 2. Manejar cambios en el formulario
  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "nombre") {
      value = value
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleCheckChange = (categoria, isChecked) => {
    const updated = { ...inscripciones };
    if (isChecked) {
      updated[categoria] = ""; // Inicializa el transponder vacío si se marca
    } else {
      delete updated[categoria]; // Elimina si se desmarca
    }
    setInscripciones(updated);
  };

  const handleTransponderChange = (categoria, value) => {
    setInscripciones({ ...inscripciones, [categoria]: value });
  };

  // Estados para controlar los modales
  const [showClearModal, setShowClearModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPassword, setExportPassword] = useState("");

  const handleVerifyAndExport = async (e) => {
    e.preventDefault();
    // Credenciales de Admin para el Basic Auth del backend
    const credentials = btoa("admin:tu_password_seguro_aqui");

    try {
      // 1. Verificar contraseña de exportación
      const verifyRes = await fetch(
        `${API_BASE_URL}/exportar/verify-excel-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${credentials}`,
          },
          body: JSON.stringify({ password: exportPassword }),
        },
      );

      if (!verifyRes.ok) {
        alert("Contraseña de exportación incorrecta.");
        return;
      }

      // 2. Si es correcta, descargar el archivo
      const exportRes = await fetch(`${API_BASE_URL}/exportar/excel`, {
        headers: { Authorization: `Basic ${credentials}` },
      });

      const blob = await exportRes.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pilotos_Sudam2027_${new Date().toLocaleDateString()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setShowExportModal(false);
      setExportPassword("");
    } catch (error) {
      alert("Error al conectar con el servidor para la exportación.");
    }
  };

  const handleClear = () => setShowClearModal(true);

  const confirmClear = () => {
    setFormData({ nombre: "", email: "", pais: "" });
    setInscripciones({});
    setMensaje({ texto: "", tipo: "" });
    setShowClearModal(false);
  };

  // 3. Guardar registro
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje({ texto: "", tipo: "" });
    setIsSaving(true);

    const categoriasSeleccionadas = Object.keys(inscripciones);

    if (categoriasSeleccionadas.length === 0) {
      setIsSaving(false);
      setMensaje({
        texto: "Debes seleccionar al menos una categoría",
        tipo: "warning",
      });
      return;
    }

    // Validar que todos los transponders seleccionados tengan valor
    if (categoriasSeleccionadas.some((cat) => !inscripciones[cat].trim())) {
      setIsSaving(false);
      setMensaje({
        texto:
          "Por favor, ingresa el transponder para todas las categorías seleccionadas",
        tipo: "warning",
      });
      return;
    }

    try {
      // Enviamos una petición por cada categoría seleccionada (normalización para el backend)
      const promesas = categoriasSeleccionadas.map(async (cat) => {
        const response = await fetch(`${API_BASE_URL}/datos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            categoria: cat,
            transponder: inscripciones[cat],
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Error al inscribir en ${cat}`);
        }
        return response.json();
      });

      await Promise.all(promesas);

      setMensaje({
        texto: "¡Inscripciones guardadas con éxito!",
        tipo: "success",
      });
      setFormData({ nombre: "", email: "", pais: "" });
      setInscripciones({});
      fetchPilotos(); // Recargar la tabla
      setIsSaving(false);
      setShowResultModal(true);
    } catch (error) {
      setIsSaving(false);
      setMensaje({
        texto: error.message || "Error de conexión con el servidor",
        tipo: "danger",
      });
      setShowResultModal(true);
    }
  };

  // 4. Lógica de Estadísticas
  // Calculamos las estadísticas basándonos siempre en la lista oficial de categorías
  const statsCategorias = categoriasDisponibles.reduce((acc, cat) => {
    acc[cat] = pilotos.filter((p) => {
      const pCat = p.categoria || "";
      if (cat === "1/8 Buggy")
        return pCat === "1/8 Buggy" || pCat === "1/8 Nitro Buggy";
      if (cat === "1/8 Truggy")
        return pCat === "1/8 Truggy" || pCat === "1/8 Nitro Truggy";
      if (cat === "1/8 Electrico")
        return pCat === "1/8 Electrico" || pCat === "1/8 E-Buggy";
      return pCat === cat;
    }).length;
    return acc;
  }, {});

  const statsPaises = pilotos.reduce((acc, p) => {
    acc[p.pais] = (acc[p.pais] || 0) + 1;
    return acc;
  }, {});

  const COLORS = [
    "#0088FE", // Azul
    "#00C49F", // Verde
    "#FFBB28", // Amarillo
    "#FF8042", // Naranja
    "#8884d8", // Púrpura
    "#82ca9d", // Verde menta
  ];

  // Función para obtener siempre el mismo color por categoría
  const getCategoryColor = (catName) => {
    if (catName === "1/8 Buggy" || catName === "1/8 Nitro Buggy")
      return COLORS[0];
    if (catName === "1/8 Truggy" || catName === "1/8 Nitro Truggy")
      return COLORS[1];
    if (catName === "1/8 Electrico" || catName === "1/8 E-Buggy")
      return COLORS[2];

    const index = categoriasDisponibles.indexOf(catName);
    return COLORS[index !== -1 ? index % COLORS.length : 0];
  };

  // Preparar datos para los gráficos
  const dataCategorias = categoriasDisponibles.map((cat) => ({
    name: cat,
    value: statsCategorias[cat] || 0,
  }));

  const dataPaises = Object.entries(statsPaises).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <Container className="py-4 mb-5">
      <h2 className="text-center mb-4">Inscripción Sudam 2027</h2>

      {/* Formulario y Estadísticas */}
      <Row className="mb-5">
        <Col lg={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-primary text-white py-3">
              <FaUserPlus className="me-2" /> Datos del Piloto
            </Card.Header>
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Completo</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    placeholder="Ej: Juan Pérez"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Correo Electrónico (Email)</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Ej: piloto@ejemplo.com"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>País</Form.Label>
                  <Form.Select
                    name="pais"
                    value={formData.pais}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecciona un país</option>
                    <option value="Chile">Chile</option>
                    <option value="Argentina">Argentina</option>
                    <option value="Brasil">Brasil</option>
                    <option value="Bolivia">Bolivia</option>
                    <option value="Ecuador">Ecuador</option>
                    <option value="Colombia">Colombia</option>
                    <option value="Paraguay">Paraguay</option>
                    <option value="Uruguay">Uruguay</option>
                    <option value="Venezuela">Venezuela</option>
                    <option value="Peru">Peru</option>
                    <option value="USA">USA</option>
                    <option value="Otro">Otro</option>
                  </Form.Select>
                </Form.Group>

                <Form.Label className="fw-bold mb-2">
                  Categorías e Inscripción
                </Form.Label>
                {categoriasDisponibles.map((cat) => (
                  <Card
                    key={cat}
                    className={`mb-2 border-0 shadow-sm transition-all ${
                      inscripciones[cat] !== undefined ? "text-white" : ""
                    }`}
                    style={{
                      borderLeft: `6px solid ${getCategoryColor(cat)}`,
                      backgroundColor:
                        inscripciones[cat] !== undefined
                          ? getCategoryColor(cat)
                          : "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    <Card.Body className="py-2 px-3">
                      <Form.Check
                        type="checkbox"
                        id={`cat-${cat}`}
                        label={cat}
                        checked={Object.prototype.hasOwnProperty.call(
                          inscripciones,
                          cat,
                        )}
                        onChange={(e) =>
                          handleCheckChange(cat, e.target.checked)
                        }
                      />
                      {inscripciones[cat] !== undefined && (
                        <Form.Control
                          size="sm"
                          className="mt-2"
                          placeholder="N° Transponder para esta categoría"
                          value={inscripciones[cat]}
                          onChange={(e) =>
                            handleTransponderChange(cat, e.target.value)
                          }
                          required
                        />
                      )}
                    </Card.Body>
                  </Card>
                ))}

                <div className="d-flex gap-2 mt-3">
                  <Button
                    variant="primary"
                    type="submit"
                    className="flex-grow-1 py-2 fw-bold shadow-sm"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Guardando...
                      </>
                    ) : (
                      "GUARDAR REGISTRO"
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleClear}
                    title="Limpiar Formulario"
                  >
                    <FaEraser />
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mt-4 mt-lg-0">
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-dark text-white py-3">
              <FaChartPie className="me-2" /> Estadísticas
            </Card.Header>
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <h1 className="display-4 fw-bold text-primary">
                  {pilotos.length}
                </h1>
                <p className="text-muted text-uppercase">Pilotos Inscritos</p>
              </div>
              <h6 className="fw-bold">
                <FaUsers className="me-2" /> Por Categoría:
              </h6>
              <div className="mb-3">
                {categoriasDisponibles.map((cat) => (
                  <Badge
                    key={cat}
                    className="me-2 mb-2 p-2 border-0 text-white shadow"
                    style={{
                      backgroundColor: getCategoryColor(cat),
                      fontSize: "0.9em",
                    }}
                  >
                    {cat}: {statsCategorias[cat] || 0}
                  </Badge>
                ))}
              </div>

              {/* Gráfico de Barras para Categorías */}
              <div style={{ width: "100%", height: 200 }} className="mb-4">
                <ResponsiveContainer>
                  <BarChart data={dataCategorias}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" fontSize={10} interval={0} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="value" position="top" fontSize={12} />
                      {dataCategorias.map((entry) => (
                        <Cell
                          key={`cell-${entry.name}`}
                          fill={getCategoryColor(entry.name)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <h6 className="fw-bold">
                <FaGlobeAmericas className="me-2" /> Por País:
              </h6>
              <div>
                {Object.entries(statsPaises).map(([pais, cant], index) => (
                  <Badge
                    key={pais}
                    className="me-2 mb-2 p-2 border-0 text-white"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  >
                    {pais}: {cant}
                  </Badge>
                ))}
              </div>

              {/* Gráfico de Dona para Países */}
              <div style={{ width: "100%", height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={dataPaises}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label
                    >
                      {dataPaises.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla de Pilotos */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Pilotos Confirmados</h3>
        <Button
          variant="success"
          onClick={() => setShowExportModal(true)}
          className="shadow-sm"
        >
          <FaFileExcel className="me-2" /> Exportar a Excel
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Table striped bordered hover responsive className="mb-0">
          <thead className="table-dark text-center">
            <tr>
              <th>Nombre</th>
              <th>País</th>
              <th style={{ width: "25%" }}>Categoría</th>
              <th style={{ width: "20%" }}>N° Transponder</th>
            </tr>
          </thead>
          <tbody className="align-middle text-center">
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center">
                  Cargando datos...
                </td>
              </tr>
            ) : pilotos.length > 0 ? (
              pilotos.map((p) => (
                <tr key={p.id}>
                  <td className="fw-bold text-start ps-3">{p.nombre}</td>
                  <td>{p.pais}</td>
                  <td>
                    <Badge
                      className="p-2 w-100 border-0 text-white"
                      style={{ backgroundColor: getCategoryColor(p.categoria) }}
                    >
                      {p.categoria}
                    </Badge>
                  </td>
                  <td>
                    <small className="text-muted fw-bold me-1">TX:</small>
                    <code
                      className="text-primary fw-bold"
                      style={{ fontSize: "1.1em" }}
                    >
                      {p.transponder}
                    </code>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  Aún no hay inscritos.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      {/* Modal para Password de Exportación */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Seguridad de Exportación</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleVerifyAndExport}>
          <Modal.Body>
            <p>
              Ingrese la contraseña autorizada para descargar la base de datos
              de pilotos.
            </p>
            <Form.Group>
              <Form.Control
                type="password"
                placeholder="Contraseña de Excel"
                value={exportPassword}
                onChange={(e) => setExportPassword(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowExportModal(false)}
            >
              Cancelar
            </Button>
            <Button variant="success" type="submit">
              Verificar y Descargar
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de Confirmación para Limpiar Formulario */}
      <Modal
        show={showClearModal}
        onHide={() => setShowClearModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Limpieza</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas limpiar el formulario? Se perderán todos
          los datos que no hayas guardado.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClearModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmClear}>
            Limpiar Todo
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Resultado de Inscripción (Guardar) */}
      <Modal
        show={showResultModal}
        onHide={() => setShowResultModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          className={
            mensaje.tipo === "success"
              ? "bg-success text-white"
              : "bg-danger text-white"
          }
        >
          <Modal.Title>
            {mensaje.tipo === "success" ? "¡Éxito!" : "Atención"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3">
            {mensaje.tipo === "success" ? (
              <div className="display-1 text-success">✓</div>
            ) : (
              <div className="display-1 text-danger">✕</div>
            )}
          </div>
          <h5>{mensaje.texto}</h5>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={mensaje.tipo === "success" ? "success" : "danger"}
            onClick={() => setShowResultModal(false)}
            className="w-100"
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
export default DataFormPage;
